import { Injectable } from '@angular/core';
import { AuthenticationService } from './authentication/authentication.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { BehaviorSubject, filter, map, Subject } from 'rxjs';
import { environment } from '@env/environment';
import { Logger } from '@app/core/logger.service';
import { Router } from '@angular/router';
import { FunctionStatus, ListenableEvents, ListenInterface, RealTimeEvents } from './signalr.events';
import { momentCustomInstance as moment } from '@projects/shared/src/app/utils/helper';

const log = new Logger('SignalR');
// import * as signalR from '@microsoft/signalr';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr';
import { FlecxModalService } from '@projects/shared/src/app/services/flecx-modal.service';
import { TelephonyProvider } from '@projects/shared/src/app/providers/telephony/telephony.provider';

@Injectable({
  providedIn: 'root',
})
export class SignalrService {
  notificationReload = new BehaviorSubject<boolean>(false);

  SampleData = new BehaviorSubject<any>('');
  Sample = this.SampleData.asObservable();

  NotificationResponseData = new BehaviorSubject<any>('');
  NotificationResponse = this.NotificationResponseData.asObservable();

  SingleRStateData = new BehaviorSubject<any>('');
  SingleRStateResponse = this.SingleRStateData.asObservable();

  GeneralNotificationResponseData = new BehaviorSubject<any>('');
  GeneralNotificationResponse = this.GeneralNotificationResponseData.asObservable();

  chatMessageEvents = new Subject<string>();
  reconnectEvent = new Subject();
  private listenSubject = new Subject<any>();

  queueService: any = [];

  private token!: string;
  public initialized = false;
  private isTokenExpired = false;
  private hubConnection!: HubConnection;
  private canSendNewRequest = true;
  private user: any;
  private eventRegistered = false;
  public isMultiTabOpen = false;
  private reconnectInterval = 2000; // retry every 2 seconds
  private reconnectTimer: any;
  private maxRetries = 86400;
  private currentRetryCount = 0;
  constructor(
    private authService: AuthenticationService,
    public toastService: ToastService,
    private router: Router,
    private flecxModalService: FlecxModalService,
    private telephony: TelephonyProvider
  ) {
    this.setupNetworkListeners();
  }
  public init() {
    const userInfo = this.authService.getUser();
    if (userInfo !== null && userInfo !== undefined) {
      if (!this.initialized) {
        this.initialized = true;
        this.user = userInfo;
        this.buildConnection();
      } else {
        log.warn('SignalR already connected');
      }
    }
  }

  public disconnect() {
    if (this.eventRegistered) {
      this.destroyListeners();
      this.hubConnection.off('onChatMessage');
      this.hubConnection.off('onLogout');
      this.hubConnection.off('sample');
      this.hubConnection.off('onGeneralNotification');
      this.hubConnection.off('onNotificationSync');
      this.hubConnection.off('sendMessageToClientUsers');
      this.hubConnection.off('sendMessageToUser');
      this.hubConnection.off('reloadApp');
      this.SampleData.next('');
      this.GeneralNotificationResponseData.next('');
      this.NotificationResponseData.next('');
      this.eventRegistered = false;
      this.hubConnection.off('OnNewTabOpen');
    }

    if (this.hubConnection && this.hubConnection.state !== HubConnectionState.Disconnected) {
      this.hubConnection.stop();
    }
    this.SingleRStateData.next(false);
  }

  private destroyListeners() {
    ListenableEvents.forEach((signature: RealTimeEvents) => {
      this.hubConnection.off(signature.toString());
    });
  }

  private setupListeners() {
    ListenableEvents.forEach((signature: RealTimeEvents) => {
      this.hubConnection.on(signature.toString(), (data: any) => {
        this.listenSubject.next({ signature, data });
      });
    });
  }

  public listen(signature: RealTimeEvents) {
    return this.listenSubject.pipe(
      filter((v: ListenInterface) => {
        if (v.signature === signature.toString()) {
          return v.data;
        }
      }),
      map((v: ListenInterface) => v.data)
    );
  }

  public invoke(signature: RealTimeEvents, ...args: any[]): Promise<any> {
    const method = signature.toString();

    return new Promise((resolve, reject) => {
      const invokeAction = () => {
        this.hubConnection
          .invoke(method, ...args)
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            reject(err);
          });
      };

      if (!this.hubConnection) {
        this.buildConnection((state: FunctionStatus) => {
          if (state === FunctionStatus.Success) {
            this.checkSignalRStausFirst(invokeAction);
          } else {
            reject(new Error('Failed to establish SignalR connection'));
          }
        });
        return;
      }

      this.checkSignalRStausFirst(invokeAction);
    });
  }
  checkSignalRStausFirst(callbackMethod: any) {
    try {
      if (!this.hubConnection || this.hubConnection.state !== HubConnectionState.Connected) {
        this.queueService.push(callbackMethod);
      } else {
        callbackMethod();
      }
    } catch (e) {
      console.log(new Date().toLocaleString() + '  ➤  Error in sending message: ' + JSON.stringify(e));
    }
  }
  enQueueEvents() {
    try {
      while (this.queueService.length > 0) {
        const event = this.queueService.shift();
        event();
      }
    } catch (e) {
      console.log(new Date().toLocaleString() + '  ➤  error in pushing singalr event ' + e);
    }
  }

  public syncNotificationData(notifications: any) {
    let loginNotificationId: string;
    let lastSyncDate = null;

    if (this.canSendNewRequest === false) {
      log.debug('SignalR --> syncNotificationData>> Skiping due to canSendNewRequest is false');
      return;
    }

    if (this.hubConnection?.state !== HubConnectionState?.Connected) {
      log.debug('SignalR --> syncNotificationData>> Skiping due to socket disconnection');
      return;
    }

    if (localStorage.getItem('loginNotificationId') != null) {
      loginNotificationId = localStorage.getItem('loginNotificationId') || '';
    } else {
      loginNotificationId = '';
    }

    if (localStorage.getItem('lastSyncDateNotify') != null) {
      lastSyncDate = localStorage.getItem('lastSyncDateNotify');
    } else {
      lastSyncDate = '';
    }

    log.debug('SignalR --> syncNotificationData ');

    this.hubConnection.invoke('syncNotificationData', notifications, loginNotificationId, lastSyncDate).catch((err) => {
      if (this.canSendNewRequest === true) {
        this.canSendNewRequest = false;
        setTimeout(() => {
          this.canSendNewRequest = true;
        }, 15000);
      }
    });
  }
  public isConnected(): boolean {
    return this.hubConnection != null && this.hubConnection.state === HubConnectionState.Connected;
  }
  public ConnectionState() {
    return this.hubConnection != null ? this.hubConnection.state : HubConnectionState.Disconnected;
  }
  private sendTestNotification() {
    if (this.canSendNewRequest === false) {
      log.debug('SignalR --> Test syncNotificationData >> this.canSendNewRequest == false');
      setTimeout(() => {
        this.sendTestNotification();
      }, 10000);
      return;
    }

    if (this.hubConnection.state !== HubConnectionState.Connected) {
      log.debug('SignalR --> Test syncNotificationData >> Skiping due to socket disconnection');
      setTimeout(() => {
        this.sendTestNotification();
      }, 10000);
      return;
    }

    log.debug('SignalR --> Test syncNotificationData ');
    this.hubConnection.invoke('syncNotificationData', null, null, null).catch((err) => {
      log.debug(err);
    });
    setTimeout(() => {
      this.sendTestNotification();
    }, 10000);
  }
  private buildConnection(callback?: Function) {
    if (localStorage.getItem('authorizationData') != null) {
      this.updateToken();
      this.authService.checkTokenExpiry();

      const logLevel = environment.production ? LogLevel.Error : LogLevel.Debug;

      const data = JSON.parse(localStorage.getItem('authorizationData') || 'null');
      if (!this.hubConnection) {
        this.hubConnection = new HubConnectionBuilder()
          .withUrl(`${environment.signalRUrl}`, {
            skipNegotiation: true,
            // tslint:disable-next-line: no-bitwise
            transport: HttpTransportType.WebSockets, // | signalR.HttpTransportType.LongPolling,
            // accessTokenFactory: () =>  data.token
            // accessTokenFactory: () => this.getToken()
            accessTokenFactory: () => this.authService.getCurrentUserTokenn(),
          })
          .withAutomaticReconnect([])
          .configureLogging(LogLevel.Debug)
          .build();
      }
      this.startConnection(callback);
    }
  }
  private getToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      resolve(this.token);
    });
  }
  private startConnection(callback?: Function) {
    if (
      this.hubConnection.state === HubConnectionState.Connected ||
      this.hubConnection.state === HubConnectionState.Connecting ||
      this.hubConnection.state === HubConnectionState.Reconnecting
    ) {
      return;
    }

    this.hubConnection
      .start()
      .then((res: any) => {
        if (typeof callback == 'function') {
          callback(FunctionStatus.Success);
        }
        this.setupListeners();
        this.registerListeners();
        this.enQueueEvents();
        this.clearReconnectState();

        this.forceTokenExpiryCheck().then(() => {});
      })
      .catch((err: any) => {
        console.log(`SignalR Error Received => ${err}`);
        if (typeof callback == 'function') {
          callback(FunctionStatus.Failed);
        }

        this.updateToken();
        this.authService.checkTokenExpiry();
        // change connection state to reconnecting
        // call the start connection
        if (localStorage.getItem('SignalrConnectivityCounts') == null) {
          localStorage.setItem('SignalrConnectivityCounts', '0');
        } else {
          let trycount: number = parseInt(localStorage.getItem('SignalrConnectivityCounts') || '0', 0);
          trycount = trycount + 1;
          localStorage.setItem('SignalrConnectivityCounts', trycount.toString());
          if (trycount === 9) {
            localStorage.setItem('IsApiNotification', 'true');
          }
        }

        // if you get error try to start connection again after 3 seconds.
        this.tryReconnect();
      });

    this.hubConnection.onclose((error) => {
      console.warn('SignalR disconnected', error);
      this.tryReconnect();
    });

    this.hubConnection.onreconnecting((error) => {
      console.log(new Date().toLocaleString() + '  ➤  SignalR attempting to reconnect...');
    });

    this.hubConnection.onreconnected(() => {
      this.enQueueEvents();
      this.clearReconnectState();
      console.log(
        `signalR is reconnected, ConnectionId : ${this.hubConnection.connectionId}, Time: ${moment().toISOString()}`
      );
      this.canSendNewRequest = true;
      this.isTokenExpired = false;
      this.reconnectEvent.next(true);
    });
  }
  private registerListeners() {
    this.SingleRStateData.next(true);
    if (this.eventRegistered) {
      return;
    }

    // consuming observables from SignalR
    this.sampleListener();
    this.onLogout();
    this.onGeneralNotification();
    this.onNotificationSync();
    this.onChatMessage();
    this.onSendMessageToClientUsers();
    this.onSendMessageToUser();
    this.reloadApp();
    this.onMultiTabOpen();
    this.eventRegistered = true;
    /*const notification: Notification[] = [];
    this.syncNotificationData(notification);*/
  }
  private async updateToken() {
    const data = JSON.parse(localStorage.getItem('authorizationData') || 'null');
    this.token = await this.authService.getToken();
  }
  private onSendMessageToClientUsers() {
    this.hubConnection.on('sendMessageToClientUsers', (data: any) => {
      // this.toastService.info(data.message);
      this.toastService.confirmDialog({
        header: 'Notification',
        message: data.message,
        rejectVisible: false,
      });
    });
  }
  private onSendMessageToUser() {
    this.hubConnection.on('sendMessageToUser', (data: any) => {
      // this.toastService.info(data.message);
      this.toastService.confirmDialog({
        header: 'Notification',
        message: data.message,
        rejectVisible: false,
      });
    });
  }
  private reloadApp() {
    this.hubConnection.on('reloadApp', (data: any) => {
      // this.notificationReload.next(true);
      this.toastService.strickMessage(
        'New version available',
        'Please click on below button to update to the new versio.'
      );
      // this.toastService.confirmDialog({
      //   header: 'New Version Notification', message: data.message, acceptLabel: 'Reload', acceptVisible: true,
      //   rejectVisible: true, rejectLabel: 'Cancel', accept: () => { window.location.reload(); }
      // });
    });
  }
  private onLogout() {
    this.hubConnection.on('onLogout', (data: any) => {
      if (this.user.Username === data) {
        console.log(
          `${new Date().toDateString()} onLogout [signalr.service.ts] line 457 => this.authService.logout() event called`
        );
        this.authService.logout();
      }
    });
  }
  private sampleListener() {
    this.hubConnection.on('sample', (response: any) => {
      this.SampleData.next(response);
    });
  }
  private onGeneralNotification() {
    this.hubConnection.on('onGeneralNotification', (data: any) => {
      this.GeneralNotificationResponseData.next(data);
    });
  }
  private onNotificationSync() {
    this.hubConnection.on('onNotificationSync', (data: any) => {
      this.NotificationResponseData.next(data);
    });
  }
  private onChatMessage() {
    this.hubConnection.on('onChatMessage', (data: any) => {
      this.chatMessageEvents.next(data);
    });
  }
  private onMultiTabOpen() {
    this.hubConnection.on('OnNewTabOpen', (data: any) => {
      this.isMultiTabOpen = true;
      this.flecxModalService.isOpen = true;
      this.flecxModalService.isSingleButton = true;
      this.flecxModalService.fButtonText = 'Use Here';
      this.flecxModalService.text = `<p>Something went wrong, seems like fleCX is already open in another tab.</p><p>You can close this window or use this tab and close other</p>`;
      this.telephony.voice.closeExistingConnections();
    });
  }

  private async forceTokenExpiryCheck() {
    // debugger;
    // when signalR reconnect we need to check if its sessions is killed or expired...
    try {
      await this.authService.loadUserProfile(true);
    } catch (error) {
      console.log(new Date().toLocaleString() + '  ➤  Error While getting User Profile . ', error);
      localStorage.removeItem('last_status');
      this.router.navigate(['logout']);
    }
  }

  private tryReconnect() {
    if (this.reconnectTimer || this.currentRetryCount >= this.maxRetries) {
      return;
    }

    this.reconnectTimer = setInterval(() => {
      if (this.hubConnection.state === HubConnectionState.Connected) {
        this.clearReconnectState();
        return;
      }
      if (
        this.hubConnection.state === HubConnectionState.Connecting ||
        this.hubConnection.state === HubConnectionState.Reconnecting
      ) {
        return;
      }

      this.currentRetryCount++;

      this.hubConnection
        .start()
        .then(() => {
          this.clearReconnectState();
          console.log(`SignalR reconnected successfully, New Connection Id = ${this.hubConnection.connectionId}`);
          this.enQueueEvents();
          this.reconnectEvent.next(true);
          this.canSendNewRequest = true;
          this.isTokenExpired = false;
        })
        .catch((err) => {
          console.warn('Reconnect attempt failed:', err);
          if (this.currentRetryCount >= this.maxRetries) {
            clearInterval(this.reconnectTimer);
            console.error('Max reconnect attempts reached');
          }
        });
    }, this.reconnectInterval);
  }

  private clearReconnectState() {
    if (this.reconnectTimer) {
      clearInterval(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.currentRetryCount = 0;
  }
  private setupNetworkListeners(): void {
    window.addEventListener('offline', () => {
      console.warn('[SignalR] Browser offline — stopping connection');
      this.hubConnection?.stop().catch((err) => console.error('Error stopping connection:', err));
    });

    window.addEventListener('online', () => {
      console.info('[SignalR] Browser online — trying to reconnect');
      this.tryReconnect();
    });
  }
}
